import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { CellStyle, ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { DraftUpdater, MonthValues } from './scenarioDraft';
import {
    generateMonthColumns,
    MONTH_KEY,
    parseBudgetNumber,
    SCENARIO_GRID_CLASS,
    SCENARIO_HEADER_HEIGHT,
    SCENARIO_ROW_HEIGHT,
    SCENARIO_MONTH_COL_WIDTH,
    scenarioFillerColumn,
    stripCurrencySymbols,
} from './helpers';

const EURO_ROW_ID = '__euro';

/** The row-label column. Narrow, because it holds exactly one caption — "Veränderung €", 88px — and its
 *  own header is deliberately blank (the section heading already says what the series is). At the shared
 *  entity width it was a 220px empty header cell over a single row of five numbers (CALL-784). */
const LABEL_COL_WIDTH = 140;

interface ScenarioBudgetAdjustmentGridProps {
    months: string[];
    /** The euro series, keyed by month. Read for RENDERING only — an edit is derived from the `previous` the
     *  updater is handed, never from this prop (see DraftUpdater). */
    monthValues: MonthValues;
    onChange: (update: DraftUpdater<MonthValues>) => void;
    isLoading: boolean;
    canEdit: boolean;
}

// Input surface for a Budget-adjustment scenario (the only budget-SIDE type): a single monthly euro row
// ("Veränderung €"; a reduction is negative). Inputs only — the calculated euro delta and its
// Relativ/Absolut translation are the section's other view.
//
// The worktype scope that used to sit under this grid moved next to the impact table (CALL-783): it scopes
// the € → contacts CONVERSION, not the euro series, so it belongs to the result and not to the input. What
// is left is a pure input grid like its four siblings.
const ScenarioBudgetAdjustmentGrid = ({
    months,
    monthValues,
    onChange,
    isLoading,
    canEdit,
}: ScenarioBudgetAdjustmentGridProps) => {
    const { t } = useTranslation('budget');

    // The euro change is a single monthly series, so the grid is one fixed row.
    const rowData = useMemo(
        () => [{ id: EURO_ROW_ID, label: t('field.budgetChange'), ...monthValues }],
        [monthValues, t]
    );

    // Reports an UPDATER of the series, not a finished map: a single monthly euro row is the likeliest of the
    // five inputs to be filled by pasting a line out of Excel, and ag-grid flushes that whole burst of
    // cellValueChanged events in ONE macrotask — so rebuilding from the `monthValues` prop kept only the last
    // month of the paste (see DraftUpdater in scenarioDraft).
    const onCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { colDef, newValue } = event;
            const month = colDef.field;
            if (!month || !MONTH_KEY.test(month)) return;
            onChange((previous) => {
                const next = { ...previous };
                // A cleared cell drops out entirely — 0 is a value the user may mean, '' is not.
                if (newValue == null || newValue === '' || Number.isNaN(Number(newValue))) delete next[month];
                else next[month] = Number(newValue);
                return next;
            });
        },
        [onChange]
    );

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(months, {
                // A `width`, not the helper's minWidth: month columns are flex:0, where ag-grid sizes by
                // width and defaults it to 200px — see SCENARIO_MONTH_COL_WIDTH.
                width: SCENARIO_MONTH_COL_WIDTH,
                editable: canEdit,
                valueParser: (params) => {
                    if (params.newValue === '' || params.newValue == null) return null;
                    return parseBudgetNumber(stripCurrencySymbols(params.newValue));
                },
                cellStyle: (): CellStyle =>
                    canEdit ? { backgroundColor: '#fffde7', textAlign: 'right' } : { textAlign: 'right' },
            }),
        [months, canEdit]
    );

    const columnDefs: ColDef[] = useMemo(() => {
        const identifier: ColDef = {
            headerName: '',
            field: 'label',
            // A `width` with flex:0, not a minWidth: a pinned column is excluded from ag-grid's flex
            // distribution but still takes the 200px `width` default when none is declared.
            width: LABEL_COL_WIDTH,
            flex: 0,
            pinned: 'left',
            editable: false,
            cellClass: 'notranslate',
        };
        // The filler soaks up the leftover width: five months plus a 140px label column filled barely half
        // of a 1487px shell (CALL-784). It has to be a column of its own rather than a stretched last month
        // — a 700px-wide "Dez. 26" printed its right-aligned euro figure 650px from its own header.
        return [identifier, ...monthColumns, scenarioFillerColumn()];
    }, [monthColumns]);

    // filter:false everywhere → the header menu (kebab) would be empty noise; hide it.
    const defaultColDef: ColDef = useMemo(
        () => ({ sortable: false, filter: false, resizable: true, suppressMenu: true }),
        []
    );

    return (
        <Box className={SCENARIO_GRID_CLASS}>
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                headerHeight={SCENARIO_HEADER_HEIGHT}
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                enableRangeSelection
                // CALL-794: commit a cell the user is still typing in when focus leaves the grid. Set per
                // grid rather than on the shared wrapper — see ScenarioRowGrid for the reasoning.
                stopEditingWhenCellsLoseFocus
                onCellValueChanged={onCellValueChanged}
            />
        </Box>
    );
};

export default ScenarioBudgetAdjustmentGrid;
